/* components/patterns/DropZone.jsx — зона загрузки файлов (DnD + клик).
   Состояния: idle / drag-over (подсветка) / uploading (прогресс по файлу).
   Данные/действия — извне: onFiles(FileList|File[]); прогресс — проп uploads[].
   Клиентская валидация размера: файлы тяжелее fileLimitMb отклоняются inline-сообщением
   (до onFiles). Квоту количества/объёма проверяет владелец (DocumentsTab) — onReject(msg).
   ХЕНДОФФ → frontend/components/common/DropZone.jsx. */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';

  const { useState, useRef } = React;

  function DropZone({ onFiles, onReject, onRetryUpload, error, accept, fileLimitMb, uploads = [], disabled = false, compact = false, className }) {
    const [dragOver, setDragOver] = useState(false);
    const [sizeError, setSizeError] = useState(null);
    const inputRef = useRef(null);
    const busy = uploads.length > 0;

    const pick = () => { if (!disabled && !busy && inputRef.current) inputRef.current.click(); };
    // отсечь файлы тяжелее лимита; принять остальные
    const accept_ = (files) => {
      setSizeError(null);
      if (!files.length) return;
      const max = fileLimitMb ? fileLimitMb * 1_000_000 : Infinity;
      const tooBig = files.filter((f) => f.size > max);
      const ok = files.filter((f) => f.size <= max);
      if (tooBig.length) {
        const names = tooBig.map((f) => f.name).join(', ');
        const msg = `Файл больше ${fileLimitMb} МБ и не загружен: ${names}`;
        setSizeError(msg);
        onReject && onReject(msg);
      }
      if (ok.length && onFiles) onFiles(ok);
    };
    const onInput = (e) => {
      accept_(Array.from(e.target.files || []));
      e.target.value = ''; // сброс — повторный выбор того же файла сработает
    };
    const onDrop = (e) => {
      e.preventDefault(); setDragOver(false);
      if (disabled || busy) return;
      accept_(Array.from(e.dataTransfer.files || []));
    };
    // Safari/Mac: без dropEffect='copy' в dragover WebKit показывает «запрещено» и не шлёт drop
    const allow = (e) => { if (!disabled && !busy) { e.preventDefault(); try { e.dataTransfer.dropEffect = 'copy'; } catch (_) {} setDragOver(true); } };

    const shownError = error || sizeError;

    return (
      <div className={'dropzone-wrap' + (className ? ' ' + className : '')}>
      <div
        className={'dropzone' + (dragOver ? ' is-dragover' : '') + (busy ? ' is-uploading' : '')
          + (disabled ? ' is-disabled' : '') + (compact ? ' dropzone--compact' : '')}
        role="button" tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={pick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
        onDragEnter={allow} onDragOver={allow}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
        onDrop={onDrop}>
        <input ref={inputRef} type="file" multiple accept={accept} className="dropzone__input"
          onChange={onInput} tabIndex={-1} aria-hidden="true" />

        {busy ? (
          <div className="dropzone__uploads">
            <div className="dropzone__uploads-head t-label">
              <Icon name="spinner" size={15} className="icon--spin" />
              Загрузка {uploads.length}…
            </div>
            {uploads.map((u) => (
              <div className={'dropzone__upload' + (u.error ? ' dropzone__upload--error' : '')} key={u.id}>
                <div className="dropzone__upload-row">
                  <span className="dropzone__upload-name truncate">{u.name}</span>
                  {u.error
                    ? <span className="dropzone__upload-pct"><Icon name="alert-circle" size={13} /> Ошибка</span>
                    : <span className="dropzone__upload-pct num">{Math.round(u.pct)}%</span>}
                </div>
                {u.error ? (
                  <div className="dropzone__upload-fail">
                    <span className="t-caption">{u.error}</span>
                    {onRetryUpload && (
                      <button type="button" className="dropzone__upload-retry"
                        onClick={(e) => { e.stopPropagation(); onRetryUpload(u.id); }}>
                        <Icon name="refresh" size={13} /> Повторить
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="dropzone__upload-track"><span className="dropzone__upload-fill" style={{ '--up-w': u.pct + '%' }} /></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            <span className="dropzone__icon"><Icon name="upload" size={compact ? 22 : 26} /></span>
            <div className="dropzone__text">
              <span className="dropzone__title">{dragOver ? 'Отпустите файлы здесь' : 'Перетащите файлы или нажмите для выбора'}</span>
              <span className="dropzone__formats t-caption">
                PDF, Word, Excel, текст, изображения{fileLimitMb ? ` · до ${fileLimitMb} МБ` : ''}
              </span>
            </div>
          </>
        )}
      </div>
      {shownError && (
        <p className="dropzone__error t-caption" role="alert">
          <Icon name="alert-circle" size={13} /> {shownError}
        </p>
      )}
      </div>
    );
  }

export { DropZone };
